import * as React from "react";
import { useRouter } from "@tanstack/react-router";
import { AlertTriangle, Info } from "lucide-react";
import { Callout } from "../../../ui/components/Callout";
import { Text } from "../../../ui/components/Text";
import { AuthTransitionScreen } from "../../../ui/components/AuthTransitionScreen";
import { DetailPageLayout } from "../layouts/DetailPageLayout";
import { CollapsibleSection } from "../../../ui/components/CollapsibleSection";
import { useSectionCollapse } from "../../hooks/useSectionCollapse";
import { useAuth } from "../../lib/auth/hooks";
import { useI18n } from "../../lib/i18n";
import { fetchAnalyticsErrorDetail, type AnalyticsErrorDetail } from "../../lib/api/product-analytics";
import { UserCell, OrganizationCell } from "./productAnalyticsEntityCells";
import { vars } from "../../../ui/styles/theme.css";
import * as s from "./ProductAnalyticsDetail.css";

const DETAIL_SECTION_ID = "detail";
const STACK_SECTION_ID = "stack";
const COLLAPSE_SCOPE = "product-analytics-error";

function formatTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

export function ProductAnalyticsErrorDetailPage({ errorId }: { errorId: string }) {
  const router = useRouter();
  const { accessToken } = useAuth();
  const { t } = useI18n();
  const { isExpanded, toggle, bindSectionLayout } = useSectionCollapse(COLLAPSE_SCOPE);

  const token =
    accessToken ??
    (typeof localStorage !== "undefined" ? localStorage.getItem("access_token") : null);

  const [detail, setDetail] = React.useState<AnalyticsErrorDetail | null>(null);
  const [loadError, setLoadError] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    if (!token) return;
    let cancelled = false;
    setLoading(true);
    fetchAnalyticsErrorDetail(token, errorId)
      .then((result) => {
        if (!cancelled) setDetail(result);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setLoadError(err instanceof Error ? err.message : t("pro.system.productAnalytics.error.loadError"));
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token, errorId, t]);

  const sectionLayout = bindSectionLayout([
    { id: DETAIL_SECTION_ID, label: t("pro.system.productAnalytics.error.details"), icon: <Info size={16} /> },
    { id: STACK_SECTION_ID, label: t("pro.system.productAnalytics.error.stackTrace"), icon: <AlertTriangle size={16} /> },
  ]);

  if (!token) {
    return (
      <div className={s.page}>
        <Callout variant="error">{t("pro.system.productAnalytics.signInRequired")}</Callout>
      </div>
    );
  }

  if (loading) {
    return <AuthTransitionScreen variant="fill" />;
  }

  if (loadError || !detail) {
    return (
      <div className={s.page}>
        <Callout variant="error">{loadError ?? t("pro.system.productAnalytics.error.notFound")}</Callout>
      </div>
    );
  }

  const { error, session } = detail;

  return (
    <DetailPageLayout
      backLink={{
        label: t("pro.system.productAnalytics.error.backToErrors"),
        onClick: () => router.history.back(),
      }}
      header={{
        media: <AlertTriangle size={28} color={vars.color.danger} />,
        title: error.message,
        subtitle: formatTime(error.occurredAt),
        badges: [
          <span key="severity" className={s.badgeDanger}>
            {error.severity}
          </span>,
          <span key="kind" className={s.badge}>
            {error.kind}
          </span>,
        ],
      }}
      contextActions={
        session
          ? [
              {
                id: "view-session",
                label: t("pro.system.productAnalytics.error.viewSession"),
                onClick: () =>
                  router.navigate({
                    to: "/system/product-analytics/sessions/$sessionId",
                    params: { sessionId: session.id },
                  }),
              },
            ]
          : undefined
      }
      {...sectionLayout}
    >
      <CollapsibleSection
        id={DETAIL_SECTION_ID}
        title={t("pro.system.productAnalytics.error.details")}
        icon={<Info size={18} />}
        expanded={isExpanded(DETAIL_SECTION_ID)}
        onToggleExpanded={() => toggle(DETAIL_SECTION_ID)}
        compactHeader
      >
        <div className={s.contextGrid}>
          <div className={s.contextField}>
            <span className={s.contextLabel}>{t("pro.system.productAnalytics.col.appSource")}</span>
            <span className={s.contextValue}>{error.appSource}</span>
          </div>
          <div className={s.contextField}>
            <span className={s.contextLabel}>{t("pro.system.productAnalytics.col.page")}</span>
            <span className={s.contextValue}>{error.urlPath ?? "—"}</span>
          </div>
          {error.httpMethod && (
            <div className={s.contextField}>
              <span className={s.contextLabel}>{t("pro.system.productAnalytics.error.httpMethod")}</span>
              <span className={s.contextValue}>{error.httpMethod}</span>
            </div>
          )}
          {error.httpStatus != null && (
            <div className={s.contextField}>
              <span className={s.contextLabel}>{t("pro.system.productAnalytics.col.httpStatus")}</span>
              <span className={s.contextValue}>{error.httpStatus}</span>
            </div>
          )}
          {error.httpUrl && (
            <div className={s.contextField}>
              <span className={s.contextLabel}>{t("pro.system.productAnalytics.error.httpUrl")}</span>
              <span className={s.contextValue}>{error.httpUrl}</span>
            </div>
          )}
          <div className={s.contextField}>
            <span className={s.contextLabel}>{t("pro.system.productAnalytics.col.user")}</span>
            <span className={s.contextValue}>
              <UserCell userId={error.userId} accessToken={token} />
            </span>
          </div>
          <div className={s.contextField}>
            <span className={s.contextLabel}>{t("pro.system.productAnalytics.col.organization")}</span>
            <span className={s.contextValue}>
              <OrganizationCell organizationId={error.organizationId} accessToken={token} />
            </span>
          </div>
          <div className={s.contextField}>
            <span className={s.contextLabel}>{t("pro.system.productAnalytics.error.fingerprint")}</span>
            <span className={s.contextValue}>{error.fingerprint ?? "—"}</span>
          </div>
        </div>
        {!session && (
          <Text level="body-small" style={{ color: vars.color.fgMuted, marginTop: 12, display: "block" }}>
            {t("pro.system.productAnalytics.error.noSession")}
          </Text>
        )}
      </CollapsibleSection>

      <CollapsibleSection
        id={STACK_SECTION_ID}
        title={t("pro.system.productAnalytics.error.stackTrace")}
        icon={<AlertTriangle size={18} />}
        expanded={isExpanded(STACK_SECTION_ID)}
        onToggleExpanded={() => toggle(STACK_SECTION_ID)}
        compactHeader
      >
        {error.stack ? (
          <pre className={s.stackTrace}>{error.stack}</pre>
        ) : (
          <Text level="body-small" style={{ color: vars.color.fgMuted }}>
            {t("pro.system.productAnalytics.error.noStack")}
          </Text>
        )}
        <Text level="body-small" style={{ color: vars.color.fgMuted, marginTop: 12, display: "block" }}>
          {t("pro.system.productAnalytics.error.sourcemapNote")}
        </Text>
      </CollapsibleSection>
    </DetailPageLayout>
  );
}
